import { withRentalProductOffer } from '../../utilities/productStructuredData'
import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Link from 'next/link'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FiDisc } from '@react-icons/all-files/fi/FiDisc'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiRefreshCw } from '@react-icons/all-files/fi/FiRefreshCw'
import { FiSliders } from '@react-icons/all-files/fi/FiSliders'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import { getActiveUser } from '../../store/slices/user'
import { SHOW_POPUP } from '../../store/slices/ui'
import { BUSINESS_HOURS } from '../../content/businessHours'
import CatalogHelp from '../../components/design-system/commerce/CatalogHelp'
import ProductCatalog from '../../components/design-system/commerce/ProductCatalog'
import Container from '../../components/design-system/foundations/Container'
import PageShell from '../../components/design-system/foundations/PageShell'
import BusinessInfoV2 from '../../components/design-system/marketing/BusinessInfoV2'
import ClosingActionBand from '../../components/design-system/marketing/ClosingActionBand'
import EventShowcase from '../../components/design-system/marketing/EventShowcase'
import FAQGroup from '../../components/design-system/marketing/FAQGroup'
import MediaFeature from '../../components/design-system/marketing/MediaFeature'
import RelatedServices from '../../components/design-system/marketing/RelatedServices'
import ReviewProof from '../../components/design-system/marketing/ReviewProof'
import ServiceCategoryGrid from '../../components/design-system/marketing/ServiceCategoryGrid'
import ServiceHero from '../../components/design-system/marketing/ServiceHero'
import ServiceIntroduction from '../../components/design-system/marketing/ServiceIntroduction'
import VisualDecisionGuide from '../../components/design-system/marketing/VisualDecisionGuide'
import QuotePop from '../../components/DeferredQuotePopup'
import * as gtag from '../../lib/gtag'
import { compactCatalogProducts } from '../../utilities/catalogPublicPayload'
import { compactPublicEvents } from '../../utilities/eventPublicPayload'
import { helper } from '../../utilities/helper'
import { isProductAvailableInMarket, MARKET_ORLANDO } from '../../utilities/fulfillmentMarket'
import { requestPageData } from '../../utilities/boundedPageData'
import orlandoDjEquipmentHeroV2 from '../../public/Pictures/categories-v2/orlando-dj-equipment-rental-hero-v2.jpg'
import djEventSetup from '../../public/Pictures/Product/dj-equipment-rental-miami.jpg'
import djOrlando from '../../public/Pictures/dj-orlando.jpg'

const SeoPageDrawer = dynamic(() => import('../../components/SeoPageDrawer'), { ssr: false })

const PAGE_PATH = '/orlando/dj-equipment-rental'
const CANONICAL = `https://miamisoundrental.com${PAGE_PATH}`
const TITLE = 'DJ Equipment Rental Orlando & Tampa | CDJs, Mixers & Controllers'
const DESCRIPTION = 'Rent DJ equipment in Orlando, including Pioneer CDJs, DJ mixers, controllers, turntables and complete setups, with Orlando pickup or Central Florida delivery.'
const ORLANDO_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Miami+Sound+Rental+Orlando+FL'

const breadcrumbs = [
  { label: 'Home', href: '/' },
  { label: 'Orlando', href: '/orlando' },
  { label: 'DJ Equipment Rental' }
]

const questions = [
  {
    question: 'Can I pick up DJ equipment from your Orlando warehouse?',
    answer: 'Yes. Eligible DJ equipment can be picked up from our Orlando branch at 8600 Commodity Circle. Choose your dates first to see live Orlando availability and the fulfillment options available for each item.'
  },
  {
    question: 'Do you deliver DJ equipment in Orlando and Tampa?',
    answer: 'Yes. Delivery and setup are available across Orlando and Central Florida. Tampa requests are fulfilled by our Orlando team when the equipment and travel window are available. Enter the event address during booking to confirm the applicable delivery price.'
  },
  {
    question: 'Can I rent Pioneer CDJs and a DJ mixer together?',
    answer: 'Yes. You can rent compatible Pioneer CDJs or media players with a DJ mixer, or choose a configured bundle when one is available. Confirm the requested models, mixer channels, media format and technical rider before reserving.'
  },
  {
    question: 'Should I rent CDJs, a DJ controller or turntables?',
    answer: 'Choose CDJs and a club-style mixer for USB-based club workflows and technical riders. A controller is useful when the DJ performs from a laptop with software such as Rekordbox or Serato. Choose turntables for vinyl playback or a rider that specifically requires them.'
  },
  {
    question: 'Are speakers included with DJ equipment?',
    answer: 'Not unless the product or bundle description specifically lists them. DJ playback equipment and the sound system solve different parts of the event. Use the Orlando Speaker Rental page to add powered speakers, subwoofers or a complete PA system.'
  },
  {
    question: 'Do the rentals include the cables and power needed to connect the DJ setup?',
    answer: 'Each product and bundle lists what is included. Tell us the playback devices, mixer, speaker system, booth position and venue power so the required USB, RCA, XLR, network, power and adapter connections can be confirmed before the event.'
  },
  {
    question: 'Can I rent DJ equipment for a wedding or private event?',
    answer: 'Yes. DJ equipment rentals are available for weddings, private events, clubs, corporate programs and other events. This page provides the equipment; it does not include a performing DJ unless that service is arranged separately.'
  },
  {
    question: 'Can I rent DJ gear for one day or on short notice?',
    answer: 'Yes, one-day and short-term DJ equipment rentals are available when inventory permits. Same-day service may also be possible. Online bookings require at least four hours of notice, including two weekday business hours; call 407 807 0052 for shorter-notice requests.'
  }
]

const DJ_CATEGORIES = [
  'DJ Equipment/Mixers',
  'DJ Equipment/CDJs',
  'DJ Equipment/Controller',
  'DJ Equipment/Bundle',
  'DJ Equipment/Turntables',
  'DJ Equipment/Effects',
  'DJ Equipment/Interfaces'
]

const isDjProduct = product => DJ_CATEGORIES.some(category => product?.categories?.includes(category))

const sanitizeDjProduct = product => ({
  ...product,
  snippets: (product?.snippets || []).filter(snippet => {
    const normalized = String(snippet || '').trim().toLowerCase()
    return normalized !== 'wattage//0w' && normalized !== 'event size//0 people'
  })
})

const absoluteProductUrl = product => (
  `https://miamisoundrental.com/products/${helper.makeUrl(product?.name || '')}`
)

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [landingResponse, productResponse, reviewResponse] = await Promise.all([
    requestPageData({
      url: `${SERVER_IP}/user/static-landing`,
      fallback: {
        newsletterevents: [],
        total_events: null
      },
      label: `landing proof for ${PAGE_PATH}`
    }),
    requestPageData({
      required: true,
      validateData: Array.isArray,
      url: `${SERVER_IP}/inventory/product`,
      params: {
        msr: true,
        cat: 'dj-equipment'
      },
      label: `products for ${PAGE_PATH}`
    }),
    requestPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: {
        page: PAGE_PATH,
        limit: 3
      },
      fallback: null,
      label: `reviews for ${PAGE_PATH}`
    })
  ])

  const products = Array.isArray(productResponse.data) ? productResponse.data : []
  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const { newsletterevents = [], total_events = null } = landingResponse.data || {}
  const usedFallback = [landingResponse, productResponse, reviewResponse]
    .some(response => response.usedFallback)

  return {
    props: {
      newsletterevents: compactPublicEvents(newsletterevents),
      total_events,
      reviewData: reviewResponse.data
    },
    revalidate: usedFallback ? 60 : 3600
  }
})

const DJEquipmentRental = ({ newsletterevents, total_events, reviewData }) => {
  const products = useSelector(getFilteredProducts)
  const user = useSelector(getActiveUser)
  const dispatch = useDispatch()
  const djProducts = products.filter(isDjProduct).map(sanitizeDjProduct)
  const localSchemaProducts = djProducts.filter(product => (
    isProductAvailableInMarket(product, MARKET_ORLANDO)
  ))

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const trackCall = () => gtag.event({
    action: 'booking_assistance_call_CTA',
    label: 'leads',
    value: 2,
    category: 'orders'
  })

  const primaryAction = {
    label: 'Browse Orlando DJ Gear',
    href: '#orlando-dj-equipment-catalog',
    icon: FiSliders,
    conversionKey: 'orlando-dj-equipment-rental.hero.browse-equipment',
    channel: 'rental',
    salesPath: 'self_service',
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }

  const secondaryAction = {
    label: 'Check Setup Compatibility',
    icon: FiMessageCircle,
    conversionKey: 'orlando-dj-equipment-rental.hero.compatibility-help',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL}#service`,
    name: 'DJ Equipment Rental in Orlando and Central Florida',
    serviceType: 'DJ equipment, CDJ, mixer, controller and turntable rental',
    url: CANONICAL,
    description: DESCRIPTION,
    provider: { '@id': 'https://miamisoundrental.com/orlando#localbusiness' },
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando DJ equipment rental options',
      itemListElement: (localSchemaProducts.length ? localSchemaProducts : djProducts)
        .slice(0, 12)
        .map(product => withRentalProductOffer({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: product.name,
            description: String(product.description || '').replace(/<[^>]*>/g, ' ').trim()
          },
          priceCurrency: 'USD',
          price: Number(product.price?.rental || 0),
          availability: 'https://schema.org/InStock',
          url: absoluteProductUrl(product)
        })).filter(Boolean)
    }
  }

  return <>
    <BreadcrumbJsonLd itemListElements={breadcrumbs.map((item, index) => ({
      position: index + 1,
      name: item.label,
      item: item.href ? `https://miamisoundrental.com${item.href}` : CANONICAL
    }))} />
    <NextSeo
      title={TITLE}
      description={DESCRIPTION}
      canonical={CANONICAL}
      twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
      openGraph={{
        type: 'website',
        url: CANONICAL,
        title: TITLE,
        description: DESCRIPTION,
        images: [{
          url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-dj-equipment-rental-hero-v2.jpg',
          width: 1600,
          height: 900,
          alt: 'Pioneer DJ controller, mixer, laptop and monitor speaker at an Orlando event venue'
        }],
        site_name: 'Miami Sound Rental'
      }}
      additionalMetaTags={[{
        name: 'keywords',
        content: 'DJ equipment rental Orlando, DJ gear rental Orlando, CDJ rental Orlando, DJ mixer rental Orlando, DJ controller rental Orlando, turntable rental Orlando, DJ equipment rental Tampa'
      }]}
    />
    <LocalBusinessJsonLd
      type='Store'
      id='https://miamisoundrental.com/orlando#localbusiness'
      name='Miami Sound Rental Orlando'
      description='Audio visual equipment rental and event production services from our Orlando branch.'
      url='https://miamisoundrental.com/orlando'
      telephone='+14078070052'
      address={{
        streetAddress: '8600 Commodity Cir #152',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32819',
        addressCountry: 'US'
      }}
      geo={{ latitude: '28.445134', longitude: '-81.431191' }}
      images={['https://miamisoundrental.com/Pictures/categories-v2/orlando-dj-equipment-rental-hero-v2.jpg']}
      openingHours={BUSINESS_HOURS}
      areaServed={[{
        geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
        geoRadius: '160000'
      }]}
    />
    <Head>
      <script
        key='orlando-dj-equipment-rental-service-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </Head>

    <PageShell>
      <ServiceHero
        breadcrumbs={breadcrumbs}
        eyebrow='DJ equipment rental · Orlando, Tampa & Central Florida'
        title='DJ Equipment Rental in Orlando'
        description='Compare Pioneer CDJs, DJ mixers, controllers, turntables and complete DJ setups. See Orlando inventory first, then choose warehouse pickup or Central Florida delivery.'
        image={{ src: orlandoDjEquipmentHeroV2, position: '68% center' }}
        imageAlt='Pioneer DJ controller, mixer, laptop and monitor speaker at an Orlando event venue'
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        processLabel='How Orlando DJ equipment rental works'
        steps={[
          { icon: 'search', title: 'Choose your workflow', description: 'CDJs, controller or turntables.' },
          { icon: 'calendar', title: 'Select dates', description: 'See live Orlando availability.' },
          { icon: 'truck', title: 'Choose fulfillment', description: 'Pickup or delivery and setup.' },
          { icon: 'success', title: 'Confirm compatibility', description: 'Review models and connections.' }
        ]}
        stackActionsOnMobile
      />

      <ServiceIntroduction
        eyebrow='Local Orlando DJ equipment inventory'
        heading='Choose DJ gear by performer workflow, technical rider and fulfillment'
        body={<>
          <p>Rent professional DJ equipment for weddings, private events, clubs, corporate programs and live performances. Compare club-style CDJs and mixers, laptop-based controllers, vinyl turntables and configured DJ bundles without mixing this page with DJ entertainment services.</p>
          <p>The catalog opens with equipment stocked by our Orlando branch. Select <strong>All</strong> only if you also want to compare identified Miami-fulfilled options when dates and travel allow.</p>
        </>}
        guideHeading='What should be confirmed before booking?'
        guideSummary='Start with the DJ&apos;s preferred playback workflow and exact model requirements.'
        guideBody={<>
          <p>Confirm Rekordbox or Serato use, laptop or USB playback, mixer channel count, turntable needs and any model-specific rider. Then identify the booth position, speaker connection, power and cable path.</p>
          <p>Eligible gear can be picked up in Orlando. Delivery and setup are available across Central Florida, including Tampa when the equipment and travel window are available.</p>
        </>}
        image={djOrlando}
        imageAlt='Pioneer CDJs and DJ mixer set up at a real event venue'
        signals={[
          'Orlando inventory is the default catalog view',
          'Club, controller and vinyl workflows are explained separately',
          'Live dates and fulfillment choices appear before checkout'
        ]}
      />

      <VisualDecisionGuide
        id='orlando-dj-workflow-guide'
        eyebrow='DJ setup decision guide'
        heading='Choose the playback workflow before choosing individual models'
        description='The correct setup depends on how the performer arrives, prepares music and connects to the sound system—not simply which product is newest.'
        items={[
          {
            label: 'USB and club-standard workflow',
            title: 'CDJs or media players with a DJ mixer',
            icon: FiDisc,
            summary: 'Use separate players and a mixer when the performer expects a club-style layout or provides a model-specific technical rider.',
            points: ['Confirm exact player and mixer models', 'Check Rekordbox export compatibility', 'Match player count and mixer channels'],
            href: '#orlando-dj-equipment-catalog',
            actionLabel: 'Compare CDJs and Mixers'
          },
          {
            label: 'Laptop-based workflow',
            title: 'DJ controller and performance software',
            icon: FiSliders,
            summary: 'Choose a controller when the DJ performs from a laptop using supported software and has confirmed the required computer connection.',
            points: ['Confirm Rekordbox or Serato support', 'Check laptop ports and adapters', 'Plan a backup playback path'],
            href: '#orlando-dj-equipment-catalog',
            actionLabel: 'Compare DJ Controllers'
          },
          {
            label: 'Vinyl or hybrid rider',
            title: 'Turntables with a compatible mixer',
            icon: FiRefreshCw,
            summary: 'Use turntables for vinyl playback or a rider that specifically requests them, with the correct cartridges, mixer inputs and isolated surface.',
            points: ['Confirm vinyl or timecode use', 'Specify cartridges and slipmats', 'Keep decks isolated from vibration'],
            href: '#orlando-dj-equipment-catalog',
            actionLabel: 'Compare Turntables'
          }
        ]}
      />

      <ProductCatalog
        carousel={{ name: 'Orlando DJ Equipment Rentals', url: CANONICAL, description: 'Pioneer CDJs, DJ mixers, controllers, turntables and complete DJ setups available to rent in Orlando' }}
        filters={[
          ['CDJs & Media Players', 'cdj'],
          ['DJ Mixers', 'mixer'],
          ['DJ Controllers', 'controller'],
          ['Turntables', 'turntable'],
          ['DJ Bundles', 'bundle']
        ]}
        sortOptions={['Release Year', 'DJ Level']}
        listName='Orlando DJ Equipment Catalog'
        anchorId='orlando-dj-equipment-catalog'
        eyebrow='CDJs, mixers, controllers, turntables and bundles'
        description='Showing DJ equipment stocked in Orlando. Select All only to include identified Miami-fulfilled options.'
        products={djProducts}
        inventoryMarket='Orlando'
        priceSummaryMode='components-and-bundles'
      />

      <Container>
        <CatalogHelp onQuote={openQuote} onCall={trackCall} phone='407 807 0052' phoneHref='tel:+14078070052' />
      </Container>

      <MediaFeature
        eyebrow='Complete the DJ signal path'
        heading='DJ gear and the sound system are planned separately'
        body={<>
          <p>The CDJs, controller, turntables and mixer create the performer&apos;s playback system. The speakers and subwoofers must still be selected for the audience, venue and program level.</p>
          <p>Tell us the DJ booth position, speaker inputs, cable distance, available power and whether the performer needs monitors. We can confirm the connections without turning this page into another speaker-rental page.</p>
        </>}
        secondaryHeading='Need the PA system too?'
        secondaryBody={<p>Use <Link href='/orlando/speaker-rental'>Orlando Speaker Rental</Link> to compare powered speakers, subwoofer bundles and complete sound-system options.</p>}
        image={djEventSetup}
        imageAlt='DJ performing with professional speakers and a DJ booth at a real event'
        reverse
      />

      <RelatedServices
        eyebrow='Related Orlando event systems'
        heading='Complete the event without confusing keyword ownership'
        description='Keep DJ playback equipment here, then use the specialist page for audience sound, staging or lighting.'
        headingId='orlando-dj-related-services'
        additionalServices={[
          { h1: 'Orlando Speaker Rental', url: '/orlando/speaker-rental', miniText: 'Choose powered speakers, subwoofers and complete PA options for the audience.' },
          { h1: 'Orlando Stage Rental', url: '/orlando/stage-rental', miniText: 'Compare modular stages, risers and configured platforms for performances and presentations.' },
          { h1: 'Event Lighting Rental', url: '/lighting-rental', miniText: 'Compare uplighting, moving lights and stage-lighting equipment available for Central Florida fulfillment.' },
          { h1: 'Orlando AV Equipment Rental', url: '/orlando', miniText: 'Return to the Orlando hub for karaoke, projectors, TVs, LED video walls and managed support.' }
        ]}
      />

      <FAQGroup
        questions={questions}
        title='Orlando DJ Equipment Rental Questions'
        headingId='orlando-dj-equipment-rental-faq-title'
        intro='Answers about Orlando inventory, Tampa delivery, CDJs, mixers, controllers, turntables, speakers, cables and short-notice rentals.'
      />
      <BusinessInfoV2 orlando sectionId='orlando-dj-branch-information' />
      <ServiceCategoryGrid
        orlando
        currentCategory='dj_equipment'
        description='Explore categories with dedicated Orlando pages, or use the Orlando hub for additional equipment available from the Central Florida branch.'
      />
      <EventShowcase
        eyebrow='Company-wide recent production work'
        heading='DJ equipment working in real event environments'
        description='These documented Miami Sound Rental projects show the kinds of venues, audiences and event systems our teams support; they are not presented as Orlando-only events.'
        filter={['party', 'concert', 'private', 'wedding', 'corporate']}
        events={newsletterevents}
        totalEvents={total_events}
        sectionId='orlando-dj-production-proof'
      />
      <ReviewProof
        filter={['party', 'convenient', 'sound']}
        heading='Recent feedback relevant to equipment and event service'
        description='Showing verified Google feedback selected for equipment-rental, event-service and Orlando relevance when available.'
        initialData={reviewData}
        pagePath={PAGE_PATH}
        reviewsUrl={ORLANDO_REVIEWS_URL}
        sectionId='orlando-dj-client-reviews'
      />
      <ClosingActionBand
        ariaLabel='Orlando DJ equipment rental options'
        eyebrow='Choose your next step with confidence'
        heading='Ready to reserve the right DJ setup?'
        headingId='orlando-dj-closing-action-heading'
        description='Compare Orlando DJ equipment online, or ask a specialist to confirm models, software, connections, delivery and the complete signal path.'
        proofItems={['Orlando inventory shown first', 'DJ workflows explained before product choice', 'Fulfillment pricing confirmed before checkout']}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </PageShell>

    {user?.email === 'pedro@drynwet.com' && user?.employee && <SeoPageDrawer pagePath={PAGE_PATH} />}
  </>
}

export default DJEquipmentRental
